import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NotificationsService } from '@core/services/notifications.service';
import { NotificationType, ToastType } from '@shared/models/notifications/notifications.models';
import { SILENT_HTTP_ERRORS } from './http-context';

interface ErrorResponse {
  type?: string;
  payload?: {
    title?: string;
    message?: string;
    status?: number;
    type?: string;
  };
  title?: string;
  message?: string;
}

export function errorInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const notificationsService = inject(NotificationsService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Static assets (SVG icons, images) are fetched through HttpClient too. A
      // missing one is a developer problem, not something to interrupt the user
      // with — it belongs in the console.
      if (!req.url.startsWith(environment.apiBaseUrl)) {
        console.warn(`Asset request failed: ${req.url} (${err.status})`);
        return throwError(() => err);
      }

      // A probe's failure is its answer. The caller still gets the error to
      // branch on; it just does not become a toast. See `SILENT_HTTP_ERRORS`.
      if (req.context.get(SILENT_HTTP_ERRORS)) {
        return throwError(() => err);
      }

      // 401s are owned by the auth interceptor (token refresh / sign-out). A raw
      // "401 Unauthorized" toast is noise on top of the real error, so suppress it
      // here and let the auth flow handle the response.
      if (err.status === 401) {
        return throwError(() => err);
      }

      const errorData = err.error as ErrorResponse | undefined;
      const errorTitle = errorData?.payload?.title || errorData?.title || 'Error';
      const errorMessage = (
        errorData?.payload?.message ||
        errorData?.message ||
        err.message ||
        'An error occurred'
      ).trim();

      notificationsService.setNotification({
        type: NotificationType.Error,
        payload: {
          type: ToastType.error,
          title: errorTitle,
          message: errorMessage,
          status: err.status,
        },
      });

      return throwError(() => err);
    })
  );
}
